import { useState } from 'react';
import { config } from '../config/app';
import { useTheme } from '../theme/store';

/*
    The deployment's mark. Falls back to the built-in envelope when the operator has not supplied
    one, and again if the file they supplied fails to load — a broken image where the company logo
    should be looks worse than no logo.

    `size` is a HEIGHT, and the width follows the artwork. It used to be both, which quietly
    squashed anything that was not square: summail's horizontal lockup went into a 30×30 box and
    came out an illegible smudge. Operators supply the mark they have, and most company marks are
    wider than they are tall.

    ⚠ A mark with live text in it — a wordmark, a lockup — must not be pointed at from here. An
    <img> loads the SVG as its own document with no access to this page's webfonts, so the text
    falls back to whatever the system has and the lockup breaks. summail's own brand pack warns
    about exactly this at the top of the file. Use the symbol, and let the product name be real
    text in the app's own typeface, which is what the line beside this renders.
*/
export default function Brand({ size = 24, of = 'product' }: { size?: number; of?: 'product' | 'operator' }) {
    const theme = useTheme((s) => s.resolved);
    const brand = config();
    const [broken, setBroken] = useState(false);

    const dark = theme === 'dark';
    const src =
        of === 'operator'
            ? ((dark ? brand.operatorLogoDark : null) ?? brand.operatorLogo)
            : ((dark ? brand.logoDark : null) ?? brand.logo);

    // An operator that supplied no logo draws nothing. The built-in envelope is the PRODUCT's
    // mark; putting it where a company's logo belongs would claim their address is ours.
    if (of === 'operator' && (!src || broken)) return null;

    if (src && !broken) {
        return (
            <img
                src={src}
                alt=""
                onError={() => setBroken(true)}
                style={{
                    height: size,
                    width: 'auto',
                    maxWidth: size * 4,
                    flex: '0 0 auto',
                    objectFit: 'contain',
                }}
            />
        );
    }

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ flex: '0 0 auto' }} aria-hidden="true">
            <rect width="24" height="24" rx="7" fill="var(--accent-bg)" />
            <g transform="translate(0 -0.7)">
                <path
                    d="M5 13h3l1 2h6l1-2h3M5 13l2-6h10l2 6v4a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 17v-4z"
                    fill="none"
                    stroke="var(--accent-strong)"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
        </svg>
    );
}
